import { useMemo, useState } from 'react';
import { EnterpriseWorkspaceNav } from '../components/enterprise/EnterpriseWorkspaceNav';
import { Card } from '../components/ui/card';
import { useEnterpriseWorkspaceCheckIns } from '../hooks/use-enterprise-workspace-checkins';

export default function EnterpriseWorkspaceTimeline() {
  const { rows, loading, error, scope } = useEnterpriseWorkspaceCheckIns();
  const [domainFilter, setDomainFilter] = useState<'all' | 'leadership' | 'friendship' | 'family' | 'romantic'>('all');
  const [riskFilter, setRiskFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');

  const filtered = useMemo(() => {
    return rows.filter((row) => {
      const domainPass = domainFilter === 'all' || row.relationshipDomain === domainFilter;
      const riskBand = row.rtiScore >= 61 ? 'high' : row.rtiScore >= 36 ? 'medium' : 'low';
      const riskPass = riskFilter === 'all' || riskBand === riskFilter;
      return domainPass && riskPass;
    });
  }, [domainFilter, riskFilter, rows]);

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#020617_0%,#0b1220_100%)]">
      <EnterpriseWorkspaceNav />
      <div className="mx-auto max-w-5xl space-y-4 px-4 py-6">
        <h1 className="text-2xl font-bold text-slate-100">Enterprise timeline</h1>
        {scope ? <p className="text-xs text-blue-300">Timeline scope: {scope}</p> : null}
        {loading ? <p className="text-xs text-slate-400">Loading server-backed timeline…</p> : null}
        {error ? <p className="text-xs text-amber-300">Server data warning: {error}</p> : null}
        <Card className="border-slate-700 bg-slate-900/80 p-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="space-y-1 text-sm text-slate-300">
              <span className="text-xs uppercase tracking-wide text-slate-400">Domain filter</span>
              <select
                value={domainFilter}
                onChange={(e) => setDomainFilter(e.target.value as 'all' | 'leadership' | 'friendship' | 'family' | 'romantic')}
                className="block rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-slate-100"
              >
                <option value="all">All domains</option>
                <option value="leadership">Leadership</option>
                <option value="friendship">Friendship</option>
                <option value="family">Family</option>
                <option value="romantic">Romantic</option>
              </select>
            </label>
            <label className="space-y-1 text-sm text-slate-300">
              <span className="text-xs uppercase tracking-wide text-slate-400">Risk filter</span>
              <select
                value={riskFilter}
                onChange={(e) => setRiskFilter(e.target.value as 'all' | 'high' | 'medium' | 'low')}
                className="block rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-slate-100"
              >
                <option value="all">All risk bands</option>
                <option value="high">High (61+)</option>
                <option value="medium">Medium (36-60)</option>
                <option value="low">Low (0-35)</option>
              </select>
            </label>
            <p className="text-sm text-slate-300">Showing {filtered.length} of {rows.length} events</p>
          </div>
        </Card>
        {filtered.length === 0 ? (
          <Card className="border-slate-700 bg-slate-900/80 p-4 text-slate-300">
            No workspace check-ins yet. Submit one from Enterprise Check-in.
          </Card>
        ) : (
          filtered.map((row) => (
            <Card key={row.id} className="border-slate-700 bg-slate-900/80 p-4">
              <p className="text-sm font-semibold text-slate-100">
                RTI {row.rtiScore} · {row.relationshipDomain}
              </p>
              <p className="mt-1 text-xs text-slate-400">{new Date(row.occurredAt).toLocaleString()}</p>
              {row.entityLabel ? <p className="mt-1 text-sm text-slate-300">Entity: {row.entityLabel}</p> : null}
              {row.note ? <p className="mt-1 text-sm text-slate-300">{row.note}</p> : null}
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

